import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Field, Hint, Row } from './editorialStyles';

const Shell = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border}; border-radius: 6px; overflow: hidden;
`;
const Toolbar = styled.div`
  display: flex; flex-wrap: wrap; gap: .25rem; padding: .45rem; border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};
  button { font: inherit; font-size: .85rem; background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.text.primary}; border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: 4px; padding: .4rem .6rem; cursor: pointer; }
`;
const Editable = styled.div`
  min-height: 320px; padding: 1rem; outline-offset: -3px !important; line-height: 1.75;
  overflow-wrap: anywhere;
  h2 { margin: 1rem 0 .5rem; font-size: 1.35rem; }
  p, ul, ol { margin: .6rem 0; }
  ul, ol { padding-left: 1.5rem; }
  a { color: ${({ theme }) => theme.colors.primary}; text-decoration: underline; }
`;

const safeLink = (value: string): boolean => /^(https?:\/\/|mailto:|tel:|\/(?!\/)|#)/i.test(value.trim());

interface Props { value: string; onChange: (value: string) => void; disabled?: boolean; }

const RichTextEditor: React.FC<Props> = ({ value, onChange, disabled }) => {
  const editor = useRef<HTMLDivElement>(null);
  const savedRange = useRef<Range | null>(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [link, setLink] = useState('');
  const [linkError, setLinkError] = useState('');

  useEffect(() => {
    if (editor.current && editor.current.innerHTML !== value) editor.current.innerHTML = value;
  }, [value]);

  const update = () => onChange(editor.current?.innerHTML || '');
  const command = (name: string, argument?: string) => {
    editor.current?.focus();
    document.execCommand(name, false, argument);
    update();
  };
  const showLink = () => {
    const selection = window.getSelection();
    savedRange.current = selection?.rangeCount && editor.current?.contains(selection.anchorNode)
      ? selection.getRangeAt(0).cloneRange() : null;
    setLinkOpen(true);
    setLinkError('');
  };
  const addLink = () => {
    if (!safeLink(link)) { setLinkError('Use um endereço https:// ou um caminho do site, como /imoveis.'); return; }
    editor.current?.focus();
    const selection = window.getSelection();
    if (savedRange.current && selection) {
      selection.removeAllRanges();
      selection.addRange(savedRange.current);
    }
    if (selection?.rangeCount && editor.current?.contains(selection.anchorNode) && !selection.isCollapsed) {
      document.execCommand('createLink', false, link.trim());
    } else if (editor.current) {
      const anchor = document.createElement('a');
      anchor.href = link.trim();
      anchor.textContent = link.trim();
      if (selection?.rangeCount && editor.current.contains(selection.anchorNode)) selection.getRangeAt(0).insertNode(anchor);
      else editor.current.appendChild(anchor);
    }
    update();
    setLinkOpen(false);
    setLink('');
  };

  return <div>
    <p id="article-content-label" style={{ fontWeight: 600, marginBottom: '.4rem' }}>Conteúdo do artigo</p>
    <Shell>
      <Toolbar role="toolbar" aria-label="Formatação do artigo">
        {[
          ['Parágrafo', 'formatBlock', 'p'], ['Subtítulo', 'formatBlock', 'h2'], ['Negrito', 'bold', ''],
          ['Itálico', 'italic', ''], ['Lista', 'insertUnorderedList', ''], ['Lista numerada', 'insertOrderedList', ''],
        ].map(([label, name, argument]) => <button key={label} type="button" disabled={disabled}
          onMouseDown={event => event.preventDefault()} onClick={() => command(name, argument)}>{label}</button>)}
        <button type="button" disabled={disabled} onMouseDown={event => event.preventDefault()} onClick={showLink}>Inserir link</button>
        <button type="button" disabled={disabled} onMouseDown={event => event.preventDefault()} onClick={() => command('unlink')}>Remover link</button>
      </Toolbar>
      <Editable ref={editor} role="textbox" aria-labelledby="article-content-label" aria-multiline="true"
        contentEditable={!disabled} suppressContentEditableWarning onInput={update}
        onFocus={() => document.execCommand?.('defaultParagraphSeparator', false, 'p')}
        onPaste={event => { event.preventDefault(); command('insertText', event.clipboardData.getData('text/plain')); }}
        onDrop={event => event.preventDefault()} />
    </Shell>
    {linkOpen && <Row style={{ marginTop: '.6rem' }}>
      <Field style={{ flex: 1 }}>Endereço do link
        <input autoFocus value={link} placeholder="https://... ou /imoveis" onChange={event => setLink(event.target.value)}
          onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); addLink(); } }} />
      </Field>
      <button type="button" onClick={addLink}>Aplicar link</button>
      <button type="button" onClick={() => setLinkOpen(false)}>Cancelar</button>
      {linkError && <p role="alert">{linkError}</p>}
    </Row>}
    <Hint>Escreva normalmente e use a barra para organizar o texto. Textos colados entram sem a formatação de origem.</Hint>
  </div>;
};

export default RichTextEditor;
